import { Grid, GridSize, Tooltip } from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import { floor, uniq } from "lodash";
import * as React from "react";
import Dropzone from "react-dropzone";
import { THEME } from "../../theme/ThemeStore";
import { t } from "../../i18n/util";
import { FileError } from "../application/DataUploadForm";
import { fromMegabytes, megabytes, mimeTypesToFileInfo } from "../util/helpers";
import { SectionHeaderSmall } from "./Primitives";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import styled from "styled-components";
import { CustomColors } from "../../types/models";

type DataUploadFieldContainerProps = {
    colors: CustomColors;
};

const DataUploadFieldContainer = styled.div<DataUploadFieldContainerProps>`
    &:hover {
        background-color: ${({ colors }) => colors.hoverColor};
    }
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 17px 17px 17px 22px;
    height: 76px;
    cursor: pointer;
    transition: background-color 250ms;
`;

type UploadFieldProps = {
    onDelete?: () => void;
    name: string;
    type: "application/pdf" | "image/jpeg" | "video/mp4" | "application/zip" | "application/x-zip-compressed";
    value?: File;
    fileSize?: number;
    tooltip?: string;
    optional?: boolean;
    onSetFileErrors?: (fileErrors: FileError[]) => void;
    onUploadFile(file: File): void;
    sm?: GridSize;
};

export const UploadField = ({
    onDelete,
    name,
    type,
    value,
    fileSize,
    tooltip,
    optional,
    onSetFileErrors,
    onUploadFile,
    sm
}: UploadFieldProps) => {
    const [rejectedFile, setRejectedFile] = React.useState<File | null>(null);
    const isMobile = useMobileMediaQuery();

    const acceptType =
        type === "application/zip" || type === "application/x-zip-compressed"
            ? ["application/zip", "application/x-zip-compressed"]
            : type;

    return (
        <Grid item xs={12} sm={sm ?? 6}>
            <Dropzone
                noClick={!!rejectedFile}
                onDrop={(acceptedFiles, fileRejections) => {
                    const tmpFileErrors: FileError[] = [];
                    if (acceptedFiles) {
                        onUploadFile(acceptedFiles[0]);
                        setRejectedFile(null);
                    }
                    if (fileRejections[0]) {
                        setRejectedFile(fileRejections[0].file);
                        uniq(fileRejections[0].errors).forEach((fileError) => {
                            if (fileError.code === "too-many-files") {
                                tmpFileErrors.push("amount");
                                setRejectedFile(null);
                            } else if (fileError.code === "file-too-large") {
                                tmpFileErrors.push("size");
                            } else if (fileError.code === "file-invalid-type") {
                                tmpFileErrors.push("type");
                            }
                        });
                    }
                    if (onSetFileErrors) {
                        onSetFileErrors(tmpFileErrors);
                    }
                }}
                accept={acceptType}
                maxFiles={1}
                maxSize={fileSize ?? megabytes(5)}
            >
                {({ getRootProps, getInputProps }) => {
                    const render = () => {
                        if (rejectedFile) {
                            return "rejectedFile";
                        } else if (value) {
                            return "acceptedFile";
                        }
                        return "empty";
                    };

                    return (
                        <DataUploadFieldContainer
                            {...getRootProps()}
                            style={{
                                border: `2px dashed ${THEME.customColors.dataUploadBorderColor}`
                            }}
                            colors={THEME.customColors}
                        >
                            <input {...getInputProps()} disabled={!!value} multiple={false} name={name} />
                            {((render() === "rejectedFile" && rejectedFile) ||
                                (render() === "acceptedFile" && value)) && (
                                <>
                                    <div style={{ display: "flex", alignItems: "center", width: "75%", gap: 16 }}>
                                        <img
                                            src={
                                                render() === "rejectedFile"
                                                    ? THEME.Images.icAchtung
                                                    : THEME.Images.icFileBlack
                                            }
                                            alt=""
                                            style={{ width: 28, height: 28 }}
                                        />
                                        <SectionHeaderSmall
                                            label={render() === "rejectedFile" ? rejectedFile?.name : value?.name}
                                            style={{
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                                whiteSpace: "nowrap"
                                            }}
                                        />
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                        <div className="caption" style={{ color: THEME.customColors.headingsDark }}>
                                            {Math.ceil(
                                                ((render() === "rejectedFile"
                                                    ? rejectedFile?.size
                                                    : value?.size) as number) / 104857.6
                                            ) / 10}{" "}
                                            {t("screen.dataUpload.mb")}
                                        </div>
                                        <img
                                            src={THEME.Images.icClose}
                                            alt=""
                                            style={{ height: 28, width: 28, cursor: "pointer" }}
                                            onClick={() => {
                                                if (onSetFileErrors) {
                                                    onSetFileErrors([]);
                                                }
                                                if (typeof onDelete === "function") {
                                                    onDelete();
                                                }
                                                if (render() === "rejectedFile") {
                                                    setRejectedFile(null);
                                                }
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                            {render() === "empty" && (
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <img
                                        src={THEME.Images.icAddTransparent}
                                        alt=""
                                        style={{
                                            width: 28,
                                            height: 28,
                                            marginRight: isMobile ? 10 : 16
                                        }}
                                    />
                                    <div className="body1semibold" style={{ marginRight: 16 }}>
                                        {name}
                                    </div>
                                    <div className="caption">
                                        {mimeTypesToFileInfo(
                                            type,
                                            fileSize ? floor(fromMegabytes(fileSize), 1) : undefined
                                        )}
                                    </div>
                                </div>
                            )}
                            {render() === "empty" && (
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    {tooltip && (
                                        <Tooltip
                                            title={
                                                <div
                                                    style={{
                                                        whiteSpace: "pre-wrap",
                                                        padding: 6,
                                                        fontSize: 12,
                                                        lineHeight: "16px"
                                                    }}
                                                >
                                                    {tooltip}
                                                </div>
                                            }
                                        >
                                            <InfoIcon
                                                style={{
                                                    height: 18,
                                                    width: 18,
                                                    fill: THEME.customColors.dataUploadBorderColor,
                                                    zIndex: 4
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                    {optional && (
                                        <div className="caption" style={{ color: THEME.customColors.body2Dark }}>
                                            {t("screen.dataUpload.optional")}
                                        </div>
                                    )}
                                </div>
                            )}
                        </DataUploadFieldContainer>
                    );
                }}
            </Dropzone>
        </Grid>
    );
};
